import React, { createContext, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type AuthFormType = 'none' | 'login' | 'register' | 'forgotPassword' | 'confirmForgotPassword';

interface AuthFormContextType {
  formType: AuthFormType;
  isOpen: boolean;
  showLoginForm: () => void;
  showRegisterForm: () => void;
  showForgotPasswordForm: () => void;
  showConfirmForgotPasswordForm: () => void;
  closeForm: () => void;
}

const AuthFormContext = createContext<AuthFormContextType | undefined>(undefined);

export const useAuthForm = () => {
  const context = useContext(AuthFormContext);
  if (!context) {
    throw new Error('useAuthForm must be used within an AuthFormProvider');
  }
  return context;
};

interface AuthFormProviderProps {
  children: React.ReactNode;
}

export const AuthFormProvider: React.FC<AuthFormProviderProps> = ({ children }) => {
  const [formType, setFormType] = useState<AuthFormType>('none');
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // MAINTENANCE MODE: Redirect all login/signup attempts to maintenance page
  const showLoginForm = () => {
    navigate('/signin');
  };

  const showRegisterForm = () => {
    navigate('/signup');
  };

  const showForgotPasswordForm = () => {
    setFormType('forgotPassword');
    setIsOpen(true);
  };

  const showConfirmForgotPasswordForm = () => {
    setFormType('confirmForgotPassword');
    setIsOpen(true);
  };

  const closeForm = () => {
    setIsOpen(false);
    setFormType('none');

    // Navigate back to home if we're on a modal-specific route
    if (location.pathname === '/signup' || location.pathname === '/signin') {
      navigate('/', { replace: true });
    }
  };

  return (
    <AuthFormContext.Provider value={{
      formType,
      isOpen,
      showLoginForm,
      showRegisterForm,
      showForgotPasswordForm,
      showConfirmForgotPasswordForm,
      closeForm
    }}>
      {children}
    </AuthFormContext.Provider>
  );
};